import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronDown, Send, Receipt, Download, Mail, Users, CheckCircle2, Plus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_BADGE = {
  paid: "bg-green-100 text-green-700",
  unpaid: "bg-red-100 text-red-700",
  partial: "bg-amber-100 text-amber-700",
};

export const Customers = ({ onAddJob }) => {
  const [view, setView] = useState("customers");
  const [custs, setCusts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [builder, setBuilder] = useState(null); // { cust, jobIds, items, markPaid, email, notes }

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/customers`).then(({ data }) => setCusts(data)).catch(() => setCusts([])),
      axios.get(`${API}/invoices`).then(({ data }) => setInvoices(data)).catch(() => setInvoices([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleCust = (key) => {
    setOpenKey(openKey === key ? null : key);
    setSelected(new Set());
  };
  const toggleJob = (jobId) => {
    const next = new Set(selected);
    next.has(jobId) ? next.delete(jobId) : next.add(jobId);
    setSelected(next);
  };

  const openBuilder = async (cust) => {
    const jobIds = selected.size ? [...selected] : cust.jobs.map((j) => j.job_id);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/invoices/preview`, { job_ids: jobIds });
      setBuilder({
        cust, jobIds,
        items: data.line_items.map((it) => ({ ...it, hours: it.hours ?? 0, rate: it.rate ?? 0 })),
        markPaid: false, email: !!cust.email, notes: "", method: "card",
        customer_email: data.customer_email, customer_name: data.customer_name,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load jobs.");
    } finally { setBusy(false); }
  };

  const setItemField = (jobId, field, value) => {
    setBuilder((b) => {
      if (!b) return b;
      const items = b.items.map((it) => {
        if (it.job_id !== jobId) return it;
        const next = { ...it, [field]: value };
        if (field === "hours" || field === "rate") {
          next.amount = Math.round((Number(next.hours || 0) * Number(next.rate || 0)) * 100) / 100;
        }
        return next;
      });
      return { ...b, items };
    });
  };

  const builderTotal = builder ? builder.items.reduce((s, it) => s + Number(it.amount || 0), 0) : 0;

  const submitBuilder = async () => {
    if (!builder) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/invoices`, {
        job_ids: builder.jobIds,
        send_email: builder.email,
        mark_paid: builder.markPaid,
        notes: builder.notes || "",
        payment_method: builder.method || "card",
        items: builder.items.map((it) => ({
          job_id: it.job_id, hours: Number(it.hours || 0), rate: Number(it.rate || 0),
          amount: Number(it.amount || 0), description: it.description, paid: builder.markPaid ? true : it.paid,
        })),
      });
      toast.success(`${builder.markPaid ? "Paid invoice" : "Invoice"} ${data.invoice_number} created${data.sent_at ? " & emailed" : ""}.`);
      setBuilder(null);
      setSelected(new Set());
      load();
      setView("invoices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invoice.");
    } finally { setBusy(false); }
  };

  const sendSummary = async (cust) => {
    if (!cust.email) { toast.error("This customer has no email on file."); return; }
    const jobIds = selected.size ? [...selected] : cust.jobs.map((j) => j.job_id);
    setBusy(true);
    try {
      await axios.post(`${API}/customers/summary`, { email: cust.email, name: cust.name, job_ids: jobIds });
      toast.success(`Summary of ${jobIds.length} job(s) emailed to ${cust.email}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send summary.");
    } finally { setBusy(false); }
  };

  const downloadPdf = async (inv) => {
    try {
      const { data } = await axios.get(`${API}/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    } catch { toast.error("Could not open PDF."); }
  };

  const resend = async (inv) => {
    setBusy(true);
    try {
      await axios.post(`${API}/invoices/${inv.id}/send`);
      toast.success(`Invoice re-emailed to ${inv.customer_email}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send invoice."); }
    finally { setBusy(false); }
  };

  const setInvStatus = async (inv, status) => {
    try {
      await axios.patch(`${API}/invoices/${inv.id}`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch { toast.error("Could not update status."); }
  };

  if (loading) {
    return <div className="flex justify-center py-24" data-testid="customers-loading"><Loader2 className="h-7 w-7 animate-spin text-blaze" /></div>;
  }

  return (
    <div className="mt-6" data-testid="customers-tab">
      <div className="flex items-center gap-2">
        {[["customers", "Customers", Users], ["invoices", "Invoices", Receipt]].map(([k, l, Icon]) => (
          <button
            key={k}
            onClick={() => setView(k)}
            data-testid={`cust-subtab-${k}`}
            className={`flex items-center gap-2 rounded-full px-4 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-all ${
              view === k ? "bg-blaze text-white shadow-md shadow-blue-500/25" : "border border-slate-300 bg-white text-slate-600 hover:border-blaze hover:text-blaze"
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {l}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${view === k ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"}`}>
              {k === "customers" ? custs.length : invoices.length}
            </span>
          </button>
        ))}
      </div>

      {view === "customers" && (
        custs.length === 0 ? (
          <p className="py-20 text-center text-sm text-slate-500" data-testid="customers-empty">No customers yet. They appear here once you have bookings.</p>
        ) : (
          <div className="mt-5 space-y-3" data-testid="customers-list">
            {custs.map((c) => {
              const open = openKey === c.key;
              return (
                <div key={c.key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid={`customer-card-${c.key}`}>
                  <button onClick={() => toggleCust(c.key)} className="flex w-full items-center gap-3 p-4 text-left" data-testid={`customer-toggle-${c.key}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate font-display text-base font-bold text-slate-900">{c.name}</p>
                        {c.job_count > 1 && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blaze">Repeat · {c.job_count}</span>}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-slate-500">{c.phone || "—"}{c.email ? ` · ${c.email}` : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">{money(c.total)}</p>
                      {c.unpaid_total > 0 && <p className="text-[11px] font-bold text-red-600">{money(c.unpaid_total)} due</p>}
                    </div>
                    <ChevronDown className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>

                  {open && (
                    <div className="border-t border-slate-100 bg-slate-50/60 p-4" data-testid={`customer-detail-${c.key}`}>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Select jobs for an invoice / summary (none = all)</p>
                      <div className="space-y-2">
                        {c.jobs.map((j) => {
                          const sel = selected.has(j.job_id);
                          return (
                            <label key={j.job_id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${sel ? "border-blaze bg-blue-50" : "border-slate-200 bg-white"}`} data-testid={`customer-job-${j.job_id}`}>
                              <input type="checkbox" checked={sel} onChange={() => toggleJob(j.job_id)} className="h-4 w-4 accent-blaze" data-testid={`customer-job-check-${j.job_id}`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-slate-900">{j.job_id}</p>
                                <p className="truncate text-xs text-slate-500">{j.moving_date || "—"} · {j.pickup_suburb} → {j.drop_suburb}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-slate-900">{money(j.amount)}</p>
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${j.paid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{j.paid ? "PAID" : "UNPAID"}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>

                      <label className="mt-4 flex items-center gap-2 text-xs font-semibold text-slate-400">
                        Hours auto-fill from what the movers logged — review &amp; edit before sending.
                      </label>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button onClick={() => openBuilder(c)} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-blaze px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white active:scale-95 disabled:opacity-50" data-testid={`create-invoice-${c.key}`}>
                          <Receipt className="h-3.5 w-3.5" /> Create Invoice {selected.size ? `(${selected.size})` : "(all)"}
                        </button>
                        <button onClick={() => sendSummary(c)} disabled={busy} className="flex items-center gap-1.5 rounded-lg border border-blaze px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-blaze active:scale-95 disabled:opacity-50" data-testid={`send-summary-${c.key}`}>
                          <Mail className="h-3.5 w-3.5" /> Email Summary
                        </button>
                        {onAddJob && (
                          <button onClick={() => onAddJob({ first_name: (c.name || "").split(" ")[0] || "", last_name: (c.name || "").split(" ").slice(1).join(" "), mobile: c.phone || "", email: c.email || "" })} className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600 active:scale-95" data-testid={`add-job-cust-${c.key}`}>
                            <Plus className="h-3.5 w-3.5" /> Add Job
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {view === "invoices" && (
        invoices.length === 0 ? (
          <p className="py-20 text-center text-sm text-slate-500" data-testid="invoices-empty">No invoices yet. Create one from the Customers tab.</p>
        ) : (
          <div className="mt-5 space-y-3" data-testid="invoices-list">
            {invoices.map((inv) => {
              const outstanding = (inv.total || 0) - (inv.paid_total || 0);
              return (
                <div key={inv.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid={`invoice-card-${inv.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display text-sm font-bold text-blaze">{inv.invoice_number}</p>
                      <p className="truncate text-sm font-semibold text-slate-900">{inv.customer_name}</p>
                      <p className="truncate text-xs text-slate-500">{inv.line_items.length} job(s) · {inv.customer_email || "no email"}{inv.sent_at ? " · sent" : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">{money(inv.total)}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_BADGE[inv.status] || STATUS_BADGE.unpaid}`}>{inv.status}</span>
                      {outstanding > 0 && <p className="mt-0.5 text-[11px] font-bold text-red-600">{money(outstanding)} due</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => downloadPdf(inv)} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-white active:scale-95" data-testid={`invoice-pdf-${inv.id}`}>
                      <Download className="h-3.5 w-3.5" /> PDF
                    </button>
                    <button onClick={() => resend(inv)} disabled={busy || !inv.customer_email} className="flex items-center gap-1.5 rounded-lg border border-blaze px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-blaze active:scale-95 disabled:opacity-40" data-testid={`invoice-send-${inv.id}`}>
                      <Send className="h-3.5 w-3.5" /> Email
                    </button>
                    {inv.status !== "paid" ? (
                      <button onClick={() => setInvStatus(inv, "paid")} className="flex items-center gap-1.5 rounded-lg border border-green-300 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-green-700 active:scale-95" data-testid={`invoice-mark-paid-${inv.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Mark Paid
                      </button>
                    ) : (
                      <button onClick={() => setInvStatus(inv, "unpaid")} className="rounded-lg border border-slate-300 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 active:scale-95" data-testid={`invoice-mark-unpaid-${inv.id}`}>
                        Mark Unpaid
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {builder && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" data-testid="invoice-builder-modal" onClick={() => !busy && setBuilder(null)}>
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="font-display text-lg font-bold text-slate-900">Build Invoice</p>
                <p className="text-xs text-slate-500">{builder.customer_name || "Customer"} · {builder.items.length} job(s)</p>
              </div>
              <button onClick={() => setBuilder(null)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" data-testid="invoice-builder-close"><X className="h-5 w-5" /></button>
            </div>

            <p className="mb-2 rounded-lg bg-blue-50 px-3 py-2 text-[11px] font-semibold text-blaze">Hours are pre-filled from what the movers logged. Edit hours or rate — the amount updates automatically.</p>

            <div className="space-y-3">
              {builder.items.map((it) => (
                <div key={it.job_id} className="rounded-xl border border-slate-200 p-3" data-testid={`builder-item-${it.job_id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-slate-900">{it.job_id}</p>
                    <p className="text-sm font-bold text-slate-900" data-testid={`builder-amount-${it.job_id}`}>{money(it.amount)}</p>
                  </div>
                  <p className="truncate text-xs text-slate-500">{it.date || "—"} · {it.description}</p>
                  <div className="mt-2 flex items-center gap-3">
                    <label className="flex flex-1 items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                      Hours
                      <input type="number" min="0" step="0.25" value={it.hours} onChange={(e) => setItemField(it.job_id, "hours", e.target.value)} className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-semibold text-slate-900 focus:border-blaze focus:outline-none" data-testid={`builder-hours-${it.job_id}`} />
                    </label>
                    <label className="flex flex-1 items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                      Rate $/hr
                      <input type="number" min="0" step="1" value={it.rate} onChange={(e) => setItemField(it.job_id, "rate", e.target.value)} className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-semibold text-slate-900 focus:border-blaze focus:outline-none" data-testid={`builder-rate-${it.job_id}`} />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Total</span>
              <span className="font-display text-lg font-bold text-slate-900" data-testid="builder-total">{money(builderTotal)}</span>
            </div>

            <div className="mt-3">
              <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Payment method</p>
              <div className="flex items-center gap-2" data-testid="builder-method-selector">
                {[
                  { k: "card", label: "Card" },
                  { k: "bank", label: "Bank Transfer" },
                  { k: "cash", label: "Cash" },
                ].map((m) => (
                  <button
                    key={m.k}
                    type="button"
                    onClick={() => setBuilder((b) => ({ ...b, method: m.k }))}
                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold uppercase tracking-wider active:scale-95 ${builder.method === m.k ? "border-blaze bg-blaze text-white" : "border-slate-300 bg-white text-slate-600"}`}
                    data-testid={`builder-method-${m.k}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={builder.markPaid} onChange={(e) => setBuilder((b) => ({ ...b, markPaid: e.target.checked }))} className="h-4 w-4 accent-green-600" data-testid="builder-mark-paid" />
                Mark as paid (paid invoice)
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={builder.email} onChange={(e) => setBuilder((b) => ({ ...b, email: e.target.checked }))} disabled={!builder.customer_email} className="h-4 w-4 accent-blaze disabled:opacity-40" data-testid="builder-email-toggle" />
                Email to {builder.customer_email || "customer (no email on file)"}
              </label>
            </div>

            <div className="mt-4 flex gap-2">
              <button onClick={() => setBuilder(null)} disabled={busy} className="flex-1 rounded-xl border border-slate-300 py-3 text-xs font-bold uppercase tracking-wider text-slate-600 active:scale-95" data-testid="builder-cancel">Cancel</button>
              <button onClick={submitBuilder} disabled={busy} className="flex flex-[2] items-center justify-center gap-1.5 rounded-xl bg-blaze py-3 text-xs font-bold uppercase tracking-wider text-white active:scale-95 disabled:opacity-50" data-testid="builder-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-3.5 w-3.5" />} {builder.markPaid ? "Create Paid Invoice" : "Create Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
